import React from 'react';
import {
  Box, Container, Grid, Typography, Button, Divider,
  IconButton, Link as MuiLink, Stack
} from '@mui/material';
import { Link } from 'react-router-dom';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
// import logo from '../../assets/hero-img.webp';
import logo from '../../assets/nds-logo.png';
import { CLINIC_INFO } from '../../data/siteData';

const QUICK_LINKS = [
  { label: 'Home', path: '/' },
  { label: 'About Us', path: '/about' },
  { label: 'Our Services', path: '/services' },
  { label: 'Patient Reviews', path: '/reviews' },
  { label: 'Contact Us', path: '/contact' },
];

export default function Footer({ onBookClick }) {
  const handleWhatsApp = () => {
    const msg = encodeURIComponent("Hello, I would like to book an appointment at Noble Dental Surgery.");
    window.open(`https://wa.me/${CLINIC_INFO.whatsapp}?text=${msg}`, '_blank');
  };

  return (
    <Box
      component="footer"
      sx={{
        background: 'linear-gradient(160deg, #0D1B2A 0%, #0A1628 50%, #071020 100%)',
        color: '#fff',
        pt: 8, pb: 3, mt: 'auto',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: 3,
          background: 'linear-gradient(90deg, #1565C0, #00C9A7, #2ECC71, #1565C0)',
        }
      }}
    >
      <Container maxWidth="xl">
        <Grid container spacing={4}>
          {/* Brand */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
              <Box component="img" src={logo} alt="Noble Dental" sx={{ height: 60, filter: 'brightness(1.1)' }} />
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ color: '#fff', lineHeight: 1.2 }}>
                  Noble Dental Surgery
                </Typography>
                <Typography variant="caption" sx={{ color: '#00C9A7', letterSpacing: 1 }}>
                  Your Smile, Our Passion
                </Typography>
              </Box>
            </Box>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mb: 2.5, lineHeight: 1.8 }}>
              Premium dental care combining cutting-edge technology with compassionate service. 
              Your perfect smile is our mission.
            </Typography>
            <Stack direction="row" spacing={1}>
              <IconButton onClick={handleWhatsApp} size="small"
                sx={{ bgcolor: '#25D366', color: '#fff', '&:hover': { bgcolor: '#20BD5C', transform: 'translateY(-2px)' }, transition: 'all 0.3s' }}>
                <WhatsAppIcon fontSize="small" />
              </IconButton>
              <IconButton href={CLINIC_INFO.instagram} target="_blank" size="small"
                sx={{ bgcolor: '#E1306C', color: '#fff', '&:hover': { bgcolor: '#C13584', transform: 'translateY(-2px)' }, transition: 'all 0.3s' }}>
                <InstagramIcon fontSize="small" />
              </IconButton>
              <IconButton href={CLINIC_INFO.facebook} target="_blank" size="small"
                sx={{ bgcolor: '#1877F2', color: '#fff', '&:hover': { bgcolor: '#166FE5', transform: 'translateY(-2px)' }, transition: 'all 0.3s' }}>
                <FacebookIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Grid>

          {/* Quick Links */}
          <Grid size={{ xs: 6, md: 2 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#00C9A7', mb: 2, letterSpacing: 0.5 }}>
              Quick Links
            </Typography>
            <Stack spacing={1}>
              {QUICK_LINKS.map(l => (
                <MuiLink
                  key={l.path}
                  component={Link}
                  to={l.path}
                  underline="none"
                  sx={{
                    color: 'rgba(255,255,255,0.65)',
                    fontSize: '0.875rem',
                    transition: 'all 0.2s',
                    '&:hover': { color: '#00C9A7', pl: 0.5 },
                    display: 'block',
                  }}
                >
                  → {l.label}
                </MuiLink>
              ))}
            </Stack>
          </Grid>

          {/* Hours */}
          <Grid size={{ xs: 6, md: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#00C9A7', mb: 2, letterSpacing: 0.5 }}>
              Working Hours
            </Typography>
            <Stack spacing={1.5}>
              {CLINIC_INFO.hours.map((h, i) => (
                <Box key={i}>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)', display: 'block' }}>
                    {h.day}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#fff', fontWeight: 500 }}>
                    {h.time}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Contact */}
          <Grid size={{ xs: 12, md: 3.5 }}>
            <Typography variant="subtitle1" fontWeight={700} sx={{ color: '#00C9A7', mb: 2, letterSpacing: 0.5 }}>
              Contact Us
            </Typography>
            <Stack spacing={2}>
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <LocationOnIcon sx={{ color: '#00C9A7', mt: 0.2, flexShrink: 0 }} fontSize="small" />
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
                  {CLINIC_INFO.address}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <PhoneIcon sx={{ color: '#00C9A7', flexShrink: 0 }} fontSize="small" />
                <MuiLink href={`tel:${CLINIC_INFO.phone}`} underline="hover"
                  sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.875rem', '&:hover': { color: '#00C9A7' } }}>
                  {CLINIC_INFO.phone}
                </MuiLink>
              </Box>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
                <EmailIcon sx={{ color: '#00C9A7', flexShrink: 0 }} fontSize="small" />
                <MuiLink href={`mailto:${CLINIC_INFO.email}`} underline="hover"
                  sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.875rem', '&:hover': { color: '#00C9A7' } }}>
                  {CLINIC_INFO.email}
                </MuiLink>
              </Box>
            </Stack>
            <Button
              variant="contained"
              color="secondary"
              fullWidth
              onClick={onBookClick}
              sx={{ mt: 3 }}
            >
              Book Appointment
            </Button>
          </Grid>
        </Grid>

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.08)', my: 4 }} />

        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
            © {new Date().getFullYear()} Noble Dental Surgery. All rights reserved.
          </Typography>
          <Typography variant="caption" component="a" sx={{ color: 'rgba(255,255,255,0.3)', textDecoration: 'none' }} href="mailto:amanpreetk567@gmail.com" >
            Designed with ❤️ for healthy smiles
          </Typography>
        </Box>
      </Container>
    </Box>
  );
}
